import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Car,
  CheckCircle2,
  Clock3,
  Gauge,
  Mail,
  MapPin,
  MessageCircle,
  Navigation,
  Phone,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID?.trim();
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY?.trim();
const EMAILJS_BOOKING_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_BOOKING_TEMPLATE_ID?.trim();
const EMAILJS_COMPLAINT_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_COMPLAINT_TEMPLATE_ID?.trim();

function hasRealEnvValue(value) {
  return Boolean(value && !value.startsWith("REPLACE_WITH_") && value.trim() !== "");
}

if (hasRealEnvValue(EMAILJS_PUBLIC_KEY)) {
  emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
}

function getEmailErrorMessage(error) {
  if (error?.text) return error.text;
  if (error?.message) return error.message;
  if (typeof error === "string") return error;
  return "Ukjent EmailJS-feil";
}

async function sendEmail({ templateID, templateParams }) {
  const payload = {
    serviceID: EMAILJS_SERVICE_ID,
    templateID,
    publicKey: EMAILJS_PUBLIC_KEY,
    templateParams,
  };

  try {
    const response = await fetch("/api/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (response.ok) return;

    const text = await response.text();
    if (!response.status || response.status === 404) {
      throw new Error("Server-endepunkt for e-post er ikke tilgjengelig.");
    }
    throw new Error(text || `Server-feil ${response.status}`);
  } catch (serverError) {
    if (!getEmailErrorMessage(serverError).includes("Server-endepunkt")) {
      throw serverError;
    }

    await emailjs.send(EMAILJS_SERVICE_ID, templateID, templateParams);
  }
}

const CITY_CONFIG = {
  Trondheim: {
    center: { lat: 63.4305, lng: 10.3951 },
    bounds: { north: 63.58, south: 63.25, east: 10.65, west: 10.1 },
    pickupPlaceholder: "Trondheim sentrum",
    destinationPlaceholder: "Værnes, hotell eller adresse",
    routeHintNo: "Trondheim sentrum til Værnes",
    routeHintEn: "Trondheim city centre to Værnes",
    phone: "+4790960331",
    sms: "+4790960331",
    whatsapp: "4790960331",
  },
  Stavanger: {
    center: { lat: 58.97, lng: 5.7331 },
    bounds: { north: 59.13, south: 58.82, east: 5.98, west: 5.48 },
    pickupPlaceholder: "Stavanger sentrum",
    destinationPlaceholder: "Stavanger lufthavn Sola, hotell eller adresse",
    routeHintNo: "Stavanger sentrum til Sola flyplass",
    routeHintEn: "Stavanger city centre to Sola Airport",
    phone: "+4790960331",
    sms: "+4790960331",
    whatsapp: "4790960331",
  },
};

const COPY = {
  no: {
    premiumTaxi: "Premium Taxi",
    lang: "no",
    fastTransparent: "Fast, transparent og elegant",
    heroTitle: "Taxi med live makspris for Trondheim og Stavanger.",
    heroText: "Beregn turen, se taksten og bestill via WhatsApp, SMS eller web-skjema uten friksjon.",
    liveBooking: "Live booking",
    whereTo: "Hvor skal du?",
    pickup: "Hentested",
    destination: "Destinasjon",
    calculate: "Beregn makspris",
    calculating: "Beregner...",
    orderNow: "Bestill nå",
    currentRate: "Takst nå",
    city: "By",
    phone: "Telefon",
    receipt: "Live kvittering",
    maxPrice: "Garantert makspris",
    routePrice: "Basert på live rute og gjeldende takst.",
    minimumPrice: "Minimumspris vises til ruten er beregnet.",
    distance: "Avstand",
    duration: "Estimert tid",
    chooseRoute: "Velg rute",
    priceFormula: "Prisformel",
    priceRule: "Prisregel",
    chooseChannel: "Velg kanal",
    webOrder: "Send web-bestilling",
    name: "Navn",
    note: "Melding",
    pickupTime: "Tidspunkt",
    asap: "Så snart som mulig",
    reserveLater: "Reserver til senere",
    reservationAvailable: "Du kan bestille nå eller reservere taxi til senere.",
    reserveButton: "Reserver",
    selectedTime: "Valgt tidspunkt",
    timeRequired: "Velg dato og klokkeslett for reservasjonen.",
    extraInfo: "Ekstra info, barnesete, bagasje eller tidspunkt.",
    sending: "Sender bestilling...",
    sent: "Bestillingen er sendt.",
    missingEmail: "Mangler EmailJS-miljøvariabler.",
    sendFailed: "Kunne ikke sende",
    airportLabel: "Fastpris til Værnes",
    airportTitle: "Trondheim sentrum og sørbyen til flyplassen.",
    airportText:
      "Gjelder turer fra Trondheim til Værnes, Stjørdal eller flystasjon. Fredag og lørdag kl. 20:00-06:00 brukes vanlig taksameterpris.",
    weekdayDay: "Man-fre 06-18",
    eveningWeekend: "Kveld/helg",
    complaints: "Klageordning",
    complaintsTitle: "Vi rydder opp ordentlig.",
    complaintsText:
      "Send en kort forklaring med kontaktinfo. Kundeservice mottar saken direkte og kan følge opp med reisenummer, tid og detaljer.",
    email: "E-post",
    whatHappened: "Hva skjedde?",
    sendSupport: "Send til kundeservice",
    supportSent: "Takk. Henvendelsen er sendt til kundeservice.",
    contact: "Kontakt oss",
    contactTitle: "Snakk direkte med Nidaros Ride.",
    contactText: "For spørsmål, endringer eller rask hjelp kan du kontakte oss på e-post, telefon, SMS eller WhatsApp.",
  },
  en: {
    premiumTaxi: "Premium Taxi",
    lang: "en",
    fastTransparent: "Fast, transparent and elegant",
    heroTitle: "Taxi with live maximum price for Trondheim and Stavanger.",
    heroText: "Calculate your trip, see the fare, and book by WhatsApp, SMS, or web order.",
    liveBooking: "Live booking",
    whereTo: "Where are you going?",
    pickup: "Pickup",
    destination: "Destination",
    calculate: "Calculate max price",
    calculating: "Calculating...",
    orderNow: "Book now",
    currentRate: "Current fare",
    city: "City",
    phone: "Phone",
    receipt: "Live receipt",
    maxPrice: "Guaranteed max price",
    routePrice: "Based on live route and current fare.",
    minimumPrice: "Minimum price is shown until the route is calculated.",
    distance: "Distance",
    duration: "Estimated time",
    chooseRoute: "Choose route",
    priceFormula: "Price formula",
    priceRule: "Price rule",
    chooseChannel: "Choose channel",
    webOrder: "Send web order",
    name: "Name",
    note: "Message",
    pickupTime: "Pickup time",
    asap: "As soon as possible",
    reserveLater: "Reserve for later",
    reservationAvailable: "You can book now or reserve a taxi for later.",
    reserveButton: "Reserve",
    selectedTime: "Selected time",
    timeRequired: "Choose date and time for the reservation.",
    extraInfo: "Extra information, child seat, luggage, or pickup time.",
    sending: "Sending order...",
    sent: "The order has been sent.",
    missingEmail: "Missing EmailJS environment variables.",
    sendFailed: "Could not send",
    airportLabel: "Fixed price to Værnes",
    airportTitle: "Trondheim city centre and south side to the airport.",
    airportText:
      "Applies to trips from Trondheim to Værnes, Stjørdal, or the air station. Friday and Saturday 20:00-06:00 use the normal taximeter fare.",
    weekdayDay: "Mon-Fri 06-18",
    eveningWeekend: "Evening/weekend",
    complaints: "Complaints",
    complaintsTitle: "We will follow up properly.",
    complaintsText:
      "Send a short explanation with contact details. Customer service receives the case directly and can follow up with trip number, time, and details.",
    email: "Email",
    whatHappened: "What happened?",
    sendSupport: "Send to customer service",
    supportSent: "Thank you. The message has been sent to customer service.",
    contact: "Contact us",
    contactTitle: "Talk directly with Nidaros Ride.",
    contactText: "For questions, changes, or quick help, contact us by email, phone, SMS, or WhatsApp.",
  },
};

const RATES = {
  dag: { label: "Dag", start: 75.4, km: 16.07, min: 12.5, minimum: 169 },
  kveld: { label: "Kveld", start: 91.23, km: 19.41, min: 14.75, minimum: 201 },
  natt: { label: "Natt", start: 96.79, km: 21.65, min: 16.57, minimum: 225 },
  helg: { label: "Helg", start: 92.02, km: 20.71, min: 14.72, minimum: 207 },
  helgnatt: { label: "Helgnatt", start: 128.51, km: 34.56, min: 27.08, minimum: 367 },
  hoytid: { label: "Høytid", start: 98.33, km: 24.56, min: 19.87, minimum: 257 },
};

const NORWEGIAN_HOLIDAYS = new Set([
  "01-01",
  "05-01",
  "05-17",
  "12-25",
  "12-26",
]);

const AIRPORT_DESTINATION_TERMS = [
  "airport",
  "lufthavn",
  "værnes",
  "vaernes",
  "flystasjon",
  "stjørdal",
  "stjordal",
  "trondheim lufthavn",
];

const SOUTH_TRONDHEIM_PICKUP_TERMS = [
  "tiller",
  "heimdal",
  "saupstad",
  "kolstad",
  "flatåsen",
  "flatasen",
  "romolslia",
  "kattem",
  "klett",
  "sjetnemarka",
  "tonstad",
  "okstad",
  "risvollan",
  "city syd",
];

function isNorwegianHoliday(date = new Date()) {
  const key = `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return NORWEGIAN_HOLIDAYS.has(key);
}

export function getTaxiRate(date = new Date()) {
  if (isNorwegianHoliday(date)) return RATES.hoytid;

  const day = date.getDay();
  const hour = date.getHours();
  const isWeekend = day === 0 || day === 6;

  if (isWeekend && hour < 6) return RATES.helgnatt;
  if (isWeekend) return RATES.helg;
  if (hour < 6) return RATES.natt;
  if (hour >= 18) return RATES.kveld;
  return RATES.dag;
}

function calculateMaxPrice(distanceKm, durationMin, date = new Date()) {
  const rate = getTaxiRate(date);
  const raw = rate.start + distanceKm * rate.km + durationMin * rate.min;
  return {
    rate,
    price: Math.ceil(Math.max(rate.minimum, raw)),
    raw,
  };
}

function normalizeSearchText(value = "") {
  return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function isAirportDestination(destination = "") {
  const normalized = normalizeSearchText(destination);
  return AIRPORT_DESTINATION_TERMS.some((term) => normalized.includes(normalizeSearchText(term)));
}

function isSouthTrondheimPickup(pickup = "") {
  const normalized = normalizeSearchText(pickup);
  return SOUTH_TRONDHEIM_PICKUP_TERMS.some((term) => normalized.includes(normalizeSearchText(term)));
}

function shouldUseAirportMeterPrice(date = new Date()) {
  const day = date.getDay();
  const hour = date.getHours();

  return (day === 5 && hour >= 20) || (day === 6 && hour < 6) || (day === 6 && hour >= 20) || (day === 0 && hour < 6);
}

function getAirportFixedPrice({ city, pickup, destination, date = new Date() }) {
  if (city !== "Trondheim" || !isAirportDestination(destination) || shouldUseAirportMeterPrice(date)) {
    return null;
  }

  const pickupZone = isSouthTrondheimPickup(pickup) ? "Sør-Trondheim" : "Sentrum";
  const hour = date.getHours();
  const day = date.getDay();
  const isWeekdayDaytime = day >= 1 && day <= 5 && hour >= 6 && hour < 18;
  const price = isWeekdayDaytime ? (pickupZone === "Sør-Trondheim" ? 799 : 699) : pickupZone === "Sør-Trondheim" ? 980 : 890;

  return {
    price,
    pickupZone,
    label: `Fastpris Værnes fra ${pickupZone}`,
    reason: isWeekdayDaytime ? "Hverdag 06-18" : "Kveld/natt/helg",
  };
}

function loadGoogleMaps() {
  if (!hasRealEnvValue(GOOGLE_MAPS_API_KEY)) {
    return Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));
  }

  if (window.google?.maps?.places) return Promise.resolve(window.google);

  const existing = document.querySelector("script[data-google-maps]");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", reject);
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.dataset.googleMaps = "true";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=no&region=NO`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function useGoogleAutocomplete(inputRef, city, onPlace) {
  useEffect(() => {
    let autocomplete;
    let listener;
    let cancelled = false;

    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current) return;
        const config = CITY_CONFIG[city];
        autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
          bounds: config.bounds,
          componentRestrictions: { country: "no" },
          fields: ["formatted_address", "geometry", "name"],
          strictBounds: false,
        });
        listener = autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          onPlace(place);
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (listener) listener.remove();
    };
  }, [city, inputRef, onPlace]);
}

function formatNok(value) {
  return new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function formatReservationTime(value, lang = "no") {
  if (!value) return lang === "en" ? "As soon as possible" : "Så snart som mulig";

  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "nb-NO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function getMinReservationDateTime() {
  const date = new Date(Date.now() + 20 * 60 * 1000);
  date.setSeconds(0, 0);
  return date.toISOString().slice(0, 16);
}

function buildTripTemplate({ city, pickup, destination, trip, lang = "no", reservationTime = "" }) {
  const english = lang === "en";
  const reservationText = formatReservationTime(reservationTime, lang);
  return [
    english ? "Hello Nidaros Ride, I would like to book a taxi." : "Hei Nidaros Ride, jeg ønsker å bestille taxi.",
    `${english ? "City" : "By"}: ${city}`,
    `${english ? "Pickup time" : "Tidspunkt"}: ${reservationText}`,
    `${english ? "Pickup" : "Henting"}: ${pickup || (english ? "Not provided" : "Ikke oppgitt")}`,
    `${english ? "Destination" : "Destinasjon"}: ${destination || (english ? "Not provided" : "Ikke oppgitt")}`,
    trip ? `${english ? "Distance" : "Avstand"}: ${trip.distanceText}` : null,
    trip ? `${english ? "Time" : "Tid"}: ${trip.durationText}` : null,
    trip ? `${english ? "Guaranteed max price" : "Garantert makspris"}: ${formatNok(trip.price)}` : null,
    trip ? `${english ? "Fare" : "Takst"}: ${trip.fixedPrice ? trip.fixedPrice.label : trip.rate.label}` : null,
    trip?.fixedPrice ? `${english ? "Price rule" : "Prisregel"}: ${trip.fixedPrice.reason}` : null,
  ]
    .filter(Boolean)
    .join("\n");
}

function buildWhatsAppUrl(phone, message) {
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

function PrimaryButton({ children, className = "", ...props }) {
  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      whileHover={{ y: -1 }}
      transition={{ duration: 0.22 }}
      className={`gold-gradient inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-extrabold text-ink shadow-gold outline-none ring-gold/40 transition focus:ring-4 disabled:cursor-not-allowed disabled:opacity-55 ${className}`}
      {...props}
    >
      {children}
    </motion.button>
  );
}

function GhostButton({ children, active, className = "", ...props }) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold transition duration-200 ${
        active
          ? "border-gold bg-gold/15 text-champagne"
          : "border-white/12 bg-white/[0.04] text-mist hover:border-white/25 hover:bg-white/[0.08]"
      } ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

function RollingPrice({ value }) {
  const digits = String(Math.max(0, Number(value) || 0)).split("");

  return (
    <div className="flex items-end justify-center gap-1 font-display text-5xl font-extrabold leading-none text-white sm:text-6xl">
      <span className="mr-1 text-2xl text-gold">kr</span>
      <AnimatePresence mode="popLayout">
        {digits.map((digit, index) => (
          <motion.span
            key={`${index}-${digit}`}
            initial={{ y: 26, opacity: 0, filter: "blur(7px)" }}
            animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
            exit={{ y: -24, opacity: 0, filter: "blur(7px)" }}
            transition={{ duration: 0.24, delay: index * 0.025 }}
            className="tabular-nums"
          >
            {digit}
          </motion.span>
        ))}
      </AnimatePresence>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.045] p-4">
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-gold">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <div className="text-lg font-extrabold text-white">{value}</div>
    </div>
  );
}

function BookingModal({ open, onClose, city, pickup, destination, trip, lang, t, reservationMode, reservationTime }) {
  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const [status, setStatus] = useState("");
  const activeReservationTime = reservationMode === "later" ? reservationTime : "";
  const template = useMemo(
    () => buildTripTemplate({ city, pickup, destination, trip, lang, reservationTime: activeReservationTime }),
    [activeReservationTime, city, destination, lang, pickup, trip]
  );
  const config = CITY_CONFIG[city];

  const sendOrder = async (event) => {
    event.preventDefault();
    setStatus(t.sending);

    if (reservationMode === "later" && !reservationTime) {
      setStatus(t.timeRequired);
      return;
    }

    if (
      !hasRealEnvValue(EMAILJS_SERVICE_ID) ||
      !hasRealEnvValue(EMAILJS_PUBLIC_KEY) ||
      !hasRealEnvValue(EMAILJS_BOOKING_TEMPLATE_ID)
    ) {
      setStatus(t.missingEmail);
      return;
    }

    try {
      await sendEmail({
        templateID: EMAILJS_BOOKING_TEMPLATE_ID,
        templateParams: {
          email: "nidarosride@gmail.com",
          to_email: "nidarosride@gmail.com",
          user_email: "nidarosride@gmail.com",
          recipient_email: "nidarosride@gmail.com",
          company_email: "nidarosride@gmail.com",
          name: form.name,
          user_name: form.name,
          from_name: form.name,
          customer_name: form.name,
          phone: form.phone,
          user_phone: form.phone,
          customer_phone: form.phone,
          pickup_time: formatReservationTime(activeReservationTime, lang),
          reservation_time: formatReservationTime(activeReservationTime, lang),
          message: template,
          notes: form.note,
          customer_note: form.note,
          trip_details: template,
        },
      });
      setStatus(t.sent);
      setForm({ name: "", phone: "", note: "" });
    } catch (error) {
      setStatus(`${t.sendFailed}: ${getEmailErrorMessage(error)}. WhatsApp/SMS is available as fallback.`);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
          onMouseDown={onClose}
        >
          <motion.div
            initial={{ y: 18, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 18, scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.24 }}
            className="glass-card max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl p-5 sm:p-7"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.22em] text-gold">{t.orderNow}</p>
                <h2 className="font-display text-3xl font-extrabold text-white">{t.chooseChannel}</h2>
              </div>
              <button
                aria-label="Lukk"
                className="rounded-full border border-white/10 bg-white/5 p-2 text-white transition hover:bg-white/10"
                onClick={onClose}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-5 rounded-xl border border-white/10 bg-ink/70 p-4 text-sm leading-6 text-mist">
              <pre className="whitespace-pre-wrap font-sans">{template}</pre>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <a
                className="gold-gradient inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-extrabold text-ink shadow-gold"
                href={buildWhatsAppUrl(config.whatsapp, template)}
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle className="h-5 w-5" />
                WhatsApp
              </a>
              <a
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.06] px-4 py-3 font-extrabold text-white transition hover:bg-white/[0.1]"
                href={`sms:${config.sms}?&body=${encodeURIComponent(template)}`}
              >
                <Phone className="h-5 w-5" />
                SMS
              </a>
            </div>

            <form className="mt-6 grid gap-4" onSubmit={sendOrder}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="input-shell">
                  <label htmlFor="order-name">{t.name}</label>
                  <input
                    id="order-name"
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    required
                  />
                </div>
                <div className="input-shell">
                  <label htmlFor="order-phone">{t.phone}</label>
                  <input
                    id="order-phone"
                    value={form.phone}
                    onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="input-shell">
                <label htmlFor="order-note">{t.note}</label>
                <textarea
                  id="order-note"
                  rows="3"
                  value={form.note}
                  onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
                  placeholder={t.extraInfo}
                />
              </div>
              <PrimaryButton type="submit" className="w-full sm:w-auto">
                <Mail className="h-5 w-5" />
                {t.webOrder}
              </PrimaryButton>
              {status && <p className="text-sm font-semibold text-champagne">{status}</p>}
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function BookingPanel({ lang, t, city, setCity }) {
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [pickupPlace, setPickupPlace] = useState(null);
  const [destinationPlace, setDestinationPlace] = useState(null);
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [reservationMode, setReservationMode] = useState("asap");
  const [reservationTime, setReservationTime] = useState("");
  const pickupRef = useRef(null);
  const destinationRef = useRef(null);
  const cityConfig = CITY_CONFIG[city];

  const handlePickupPlace = useCallback((place) => {
    setPickup(place.formatted_address || place.name || "");
    setPickupPlace(place);
  }, []);

  const handleDestinationPlace = useCallback((place) => {
    setDestination(place.formatted_address || place.name || "");
    setDestinationPlace(place);
  }, []);

  useGoogleAutocomplete(pickupRef, city, handlePickupPlace);
  useGoogleAutocomplete(destinationRef, city, handleDestinationPlace);

  const calculateRoute = async () => {
    setError("");

    if (reservationMode === "later" && !reservationTime) {
      setError(t.timeRequired);
      return false;
    }

    setLoading(true);

    try {
      const google = await loadGoogleMaps();
      const origin = pickupPlace?.geometry?.location || pickup;
      const target = destinationPlace?.geometry?.location || destination;

      if (!origin || !target) {
        setError("Legg inn henting og destinasjon for a beregne pris.");
        return;
      }

      const service = new google.maps.DistanceMatrixService();
      const response = await service.getDistanceMatrix({
        origins: [origin],
        destinations: [target],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: google.maps.TrafficModel.BEST_GUESS,
        },
      });

      const element = response.rows?.[0]?.elements?.[0];
      if (!element || element.status !== "OK") {
        setError("Fant ikke en kjørevei mellom adressene.");
        return;
      }

      const distanceKm = element.distance.value / 1000;
      const durationMin = (element.duration_in_traffic?.value || element.duration.value) / 60;
      const pricingDate = reservationMode === "later" && reservationTime ? new Date(reservationTime) : new Date();
      const fixedPrice = getAirportFixedPrice({ city, pickup, destination, date: pricingDate });
      const calculated = calculateMaxPrice(distanceKm, durationMin, pricingDate);

      setTrip({
        distanceKm,
        durationMin,
        distanceText: element.distance.text,
        durationText: element.duration_in_traffic?.text || element.duration.text,
        price: fixedPrice?.price || calculated.price,
        rate: calculated.rate,
        fixedPrice,
        pricingDate,
      });
      return true;
    } catch (routeError) {
      setError(
        hasRealEnvValue(GOOGLE_MAPS_API_KEY)
          ? "Kunne ikke hente rute akkurat nå."
          : "Mangler Google Maps API-nøkkel."
      );
    } finally {
      setLoading(false);
    }

    return false;
  };

  const reserveTrip = async () => {
    setReservationMode("later");
    if (!reservationTime) {
      setError(t.timeRequired);
      return;
    }

    const calculated = await calculateRoute();
    if (calculated !== false) setModalOpen(true);
  };

  const currentRate = getTaxiRate(reservationMode === "later" && reservationTime ? new Date(reservationTime) : new Date());

  return (
    <section className="grid items-start gap-5 lg:grid-cols-[1.08fr_0.92fr]">
      <motion.div
        initial={{ y: 18, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.28 }}
        className="glass-card rounded-2xl p-5 sm:p-7"
      >
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-gold">{t.liveBooking}</p>
            <h2 className="mt-1 font-display text-3xl font-extrabold text-white">{t.whereTo}</h2>
          </div>
          <div className="flex rounded-2xl border border-white/10 bg-white/[0.04] p-1">
            {Object.keys(CITY_CONFIG).map((option) => (
              <button
                key={option}
                onClick={() => {
                  setCity(option);
                  setPickup("");
                  setDestination("");
                  setPickupPlace(null);
                  setDestinationPlace(null);
                  setTrip(null);
                }}
                className={`rounded-xl px-4 py-2 text-sm font-extrabold transition ${
                  city === option ? "bg-gold text-ink" : "text-mist hover:bg-white/[0.08]"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4">
          <div className="input-shell">
            <div className="mb-2 flex items-end justify-between gap-3">
              <label htmlFor="pickup" className="!mb-0">{t.pickup}</label>
              <span className="text-xs font-semibold text-champagne/80">{lang === "en" ? cityConfig.routeHintEn : cityConfig.routeHintNo}</span>
            </div>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gold" />
              <input
                id="pickup"
                ref={pickupRef}
                value={pickup}
                onChange={(event) => {
                  setPickup(event.target.value);
                  setPickupPlace(null);
                }}
                className="!pl-12"
                placeholder={cityConfig.pickupPlaceholder}
              />
            </div>
          </div>
          <div className="input-shell">
            <div className="mb-2 flex items-end justify-between gap-3">
              <label htmlFor="destination" className="!mb-0">{t.destination}</label>
              <span className="text-xs font-semibold text-champagne/80">{cityConfig.destinationPlaceholder}</span>
            </div>
            <div className="relative">
              <Navigation className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gold" />
              <input
                id="destination"
                ref={destinationRef}
                value={destination}
                onChange={(event) => {
                  setDestination(event.target.value);
                  setDestinationPlace(null);
                }}
                className="!pl-12"
                placeholder={cityConfig.destinationPlaceholder}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-red-300/20 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-100">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 rounded-xl border border-gold/20 bg-gold/10 px-4 py-3 text-sm font-bold text-champagne">
            <Clock3 className="h-5 w-5 shrink-0 text-gold" />
            <span>{t.reservationAvailable}</span>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.045] p-4">
            <label className="mb-3 block text-sm font-bold text-mist/80">{t.pickupTime}</label>
            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              <GhostButton
                type="button"
                active={reservationMode === "asap"}
                onClick={() => {
                  setReservationMode("asap");
                  setReservationTime("");
                  setTrip(null);
                }}
              >
                <Clock3 className="h-4 w-4" />
                {t.asap}
              </GhostButton>
              <GhostButton
                type="button"
                active={reservationMode === "later"}
                onClick={() => {
                  setReservationMode("later");
                  setTrip(null);
                }}
              >
                <Clock3 className="h-4 w-4" />
                {t.reserveLater}
              </GhostButton>
            </div>
            {reservationMode === "later" && (
              <div className="input-shell">
                <label htmlFor="reservation-time">{t.selectedTime}</label>
                <input
                  id="reservation-time"
                  type="datetime-local"
                  min={getMinReservationDateTime()}
                  value={reservationTime}
                  onChange={(event) => {
                    setReservationTime(event.target.value);
                    setTrip(null);
                  }}
                  required
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <PrimaryButton onClick={calculateRoute} disabled={loading} className="flex-1">
              <Gauge className="h-5 w-5" />
              {loading ? t.calculating : t.calculate}
            </PrimaryButton>
            <GhostButton onClick={() => setModalOpen(true)} disabled={!pickup && !destination} className="flex-1">
              <Car className="h-5 w-5" />
              {t.orderNow}
            </GhostButton>
            <GhostButton onClick={reserveTrip} disabled={loading || (!pickup && !destination)} className="flex-1">
              <Clock3 className="h-5 w-5" />
              {t.reserveButton}
            </GhostButton>
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <Stat icon={ShieldCheck} label={t.currentRate} value={currentRate.label} />
          <Stat icon={MapPin} label={t.city} value={city} />
          <Stat icon={Phone} label={t.phone} value={CITY_CONFIG[city].phone} />
        </div>
      </motion.div>

      <motion.aside
        initial={{ y: 18, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.28, delay: 0.08 }}
        className="glass-card rounded-2xl p-5 sm:p-7"
      >
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-gold">{t.receipt}</p>
            <h2 className="mt-1 font-display text-3xl font-extrabold text-white">{t.maxPrice}</h2>
          </div>
          <ReceiptText className="h-9 w-9 text-gold" />
        </div>

        <div className="rounded-2xl border border-gold/20 bg-gold/10 p-5 text-center">
          <RollingPrice value={trip?.price || currentRate.minimum} />
          <p className="mt-3 text-sm font-semibold text-champagne">
            {trip?.fixedPrice
              ? `${trip.fixedPrice.label}. ${trip.fixedPrice.reason}.`
              : trip
                ? t.routePrice
                : t.minimumPrice}
          </p>
        </div>

        <div className="mt-5 grid gap-3">
          <Stat icon={Clock3} label={t.pickupTime} value={formatReservationTime(reservationMode === "later" ? reservationTime : "", lang)} />
          <Stat icon={Navigation} label={t.distance} value={trip?.distanceText || t.chooseRoute} />
          <Stat icon={Clock3} label={t.duration} value={trip?.durationText || t.chooseRoute} />
          <Stat
            icon={Sparkles}
            label={trip?.fixedPrice ? t.priceRule : t.priceFormula}
            value={
              trip?.fixedPrice
                ? `${trip.fixedPrice.pickupZone} til Værnes`
                : `Start ${currentRate.start} + km ${currentRate.km} + min ${currentRate.min}`
            }
          />
        </div>

        <PrimaryButton className="mt-5 w-full" onClick={() => setModalOpen(true)} disabled={!pickup && !destination}>
          {t.orderNow}
          <ArrowRight className="h-5 w-5" />
        </PrimaryButton>
      </motion.aside>

      <BookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        city={city}
        pickup={pickup}
        destination={destination}
        trip={trip}
        lang={lang}
        t={t}
        reservationMode={reservationMode}
        reservationTime={reservationTime}
      />
    </section>
  );
}

function AirportPriceBand({ t }) {
  const prices = [
    { zone: "Sentrum", day: "699 kr", evening: "890 kr" },
    { zone: "Sør-Trondheim", day: "799 kr", evening: "980 kr" },
  ];

  return (
    <section className="mb-8 rounded-2xl border border-gold/25 bg-gold/[0.08] p-5 shadow-gold backdrop-blur sm:p-6">
      <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-gold">{t.airportLabel}</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold text-white sm:text-4xl">{t.airportTitle}</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-mist/82">{t.airportText}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {prices.map((price) => (
            <div key={price.zone} className="rounded-xl border border-white/12 bg-ink/65 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="font-extrabold text-white">{price.zone}</span>
                <Car className="h-5 w-5 text-gold" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-white/[0.055] p-3">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-gold">{t.weekdayDay}</p>
                  <p className="mt-1 text-2xl font-extrabold text-white">{price.day}</p>
                </div>
                <div className="rounded-lg bg-white/[0.055] p-3">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-gold">{t.eveningWeekend}</p>
                  <p className="mt-1 text-2xl font-extrabold text-white">{price.evening}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComplaintsForm({ t }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [status, setStatus] = useState("");

  const submitComplaint = async (event) => {
    event.preventDefault();
    setStatus(t.sending);

    if (
      !hasRealEnvValue(EMAILJS_SERVICE_ID) ||
      !hasRealEnvValue(EMAILJS_PUBLIC_KEY) ||
      !hasRealEnvValue(EMAILJS_COMPLAINT_TEMPLATE_ID)
    ) {
      setStatus(t.missingEmail);
      return;
    }

    try {
      await sendEmail({
        templateID: EMAILJS_COMPLAINT_TEMPLATE_ID,
        templateParams: {
          email: "kunderservice@nidarosridetaxi.no",
          to_email: "kunderservice@nidarosridetaxi.no",
          user_email: form.email,
          recipient_email: "kunderservice@nidarosridetaxi.no",
          company_email: "kunderservice@nidarosridetaxi.no",
          name: form.name,
          user_name: form.name,
          from_name: form.name,
          customer_name: form.name,
          phone: form.phone,
          user_phone: form.phone,
          customer_phone: form.phone,
          reply_to: form.email,
          from_email: form.email,
          customer_email: form.email,
          message: form.message,
          complaint_message: form.message,
        },
      });
      setStatus(t.supportSent);
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (error) {
      setStatus(`${t.sendFailed}: ${getEmailErrorMessage(error)}.`);
    }
  };

  return (
    <section className="mt-16 grid gap-6 border-t border-white/10 py-10 lg:grid-cols-[0.8fr_1.2fr]">
      <div>
        <p className="text-sm font-bold uppercase tracking-[0.22em] text-gold">{t.complaints}</p>
        <h2 className="mt-2 font-display text-3xl font-extrabold text-white">{t.complaintsTitle}</h2>
        <p className="mt-3 max-w-xl text-mist/80">{t.complaintsText}</p>
      </div>
      <form className="glass-card grid gap-4 rounded-2xl p-5 sm:p-7" onSubmit={submitComplaint}>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="input-shell">
            <label htmlFor="complaint-name">{t.name}</label>
            <input
              id="complaint-name"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              required
            />
          </div>
          <div className="input-shell">
            <label htmlFor="complaint-phone">{t.phone}</label>
            <input
              id="complaint-phone"
              value={form.phone}
              onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
            />
          </div>
          <div className="input-shell">
            <label htmlFor="complaint-email">{t.email}</label>
            <input
              id="complaint-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              required
            />
          </div>
        </div>
        <div className="input-shell">
          <label htmlFor="complaint-message">{t.whatHappened}</label>
          <textarea
            id="complaint-message"
            rows="4"
            value={form.message}
            onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))}
            required
          />
        </div>
        <PrimaryButton type="submit" className="w-full sm:w-auto">
          {t.sendSupport}
          <ArrowRight className="h-5 w-5" />
        </PrimaryButton>
        {status && <p className="text-sm font-semibold text-champagne">{status}</p>}
      </form>
    </section>
  );
}

function ContactSection({ t }) {
  const phone = CITY_CONFIG.Trondheim.phone;
  const sms = CITY_CONFIG.Trondheim.sms;
  const whatsapp = CITY_CONFIG.Trondheim.whatsapp;
  const email = "kundeservice@nidarosridetaxi.no";
  const message = t.lang === "en" ? "Hello Nidaros Ride, I need help." : "Hei Nidaros Ride, jeg trenger hjelp.";

  return (
    <section className="mt-12 rounded-2xl border border-white/10 bg-white/[0.045] p-5 backdrop-blur sm:p-7">
      <div className="mb-5">
        <p className="text-sm font-bold uppercase tracking-[0.22em] text-gold">{t.contact}</p>
        <h2 className="mt-2 font-display text-3xl font-extrabold text-white">{t.contactTitle}</h2>
        <p className="mt-3 max-w-2xl text-mist/80">{t.contactText}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <a
          className="rounded-xl border border-white/12 bg-ink/65 p-4 transition hover:border-gold/50 hover:bg-gold/10"
          href={`mailto:${email}`}
        >
          <Mail className="mb-3 h-5 w-5 text-gold" />
          <p className="text-sm font-bold text-mist/70">Email</p>
          <p className="break-words font-extrabold text-white">{email}</p>
        </a>
        <a
          className="rounded-xl border border-white/12 bg-ink/65 p-4 transition hover:border-gold/50 hover:bg-gold/10"
          href={`tel:${phone}`}
        >
          <Phone className="mb-3 h-5 w-5 text-gold" />
          <p className="text-sm font-bold text-mist/70">{t.phone}</p>
          <p className="font-extrabold text-white">{phone}</p>
        </a>
        <a
          className="rounded-xl border border-white/12 bg-ink/65 p-4 transition hover:border-gold/50 hover:bg-gold/10"
          href={`sms:${sms}?&body=${encodeURIComponent(message)}`}
        >
          <MessageCircle className="mb-3 h-5 w-5 text-gold" />
          <p className="text-sm font-bold text-mist/70">SMS</p>
          <p className="font-extrabold text-white">{phone}</p>
        </a>
        <a
          className="rounded-xl border border-white/12 bg-ink/65 p-4 transition hover:border-gold/50 hover:bg-gold/10"
          href={buildWhatsAppUrl(whatsapp, message)}
          target="_blank"
          rel="noreferrer"
        >
          <MessageCircle className="mb-3 h-5 w-5 text-gold" />
          <p className="text-sm font-bold text-mist/70">WhatsApp</p>
          <p className="font-extrabold text-white">{phone}</p>
        </a>
      </div>
    </section>
  );
}

export default function App() {
  const [lang, setLang] = useState("no");
  const [city, setCity] = useState("Trondheim");
  const t = COPY[lang];

  return (
    <main className="relative min-h-screen overflow-hidden px-4 py-6 sm:px-6 lg:px-8">
      <div className="ambient-orb left-[6%] top-[12%] h-52 w-52 animate-drift bg-gold/40" />
      <div className="ambient-orb right-[8%] top-[24%] h-64 w-64 animate-drift bg-cyan-400/20 [animation-delay:1.2s]" />
      <div className="ambient-orb bottom-[10%] left-[35%] h-56 w-56 animate-drift bg-indigo-400/20 [animation-delay:2.1s]" />

      <div className="relative z-10 mx-auto max-w-7xl">
        <header className="mb-8 flex flex-col gap-5 rounded-2xl border border-white/10 bg-white/[0.035] p-5 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-2xl border border-gold/40 bg-gold/15">
              <Car className="h-7 w-7 text-gold" />
            </div>
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.28em] text-gold">{t.premiumTaxi}</p>
              <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Nidaros Ride</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex rounded-2xl border border-white/10 bg-white/[0.04] p-1">
              {[
                ["no", "NO"],
                ["en", "EN"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setLang(value)}
                  className={`rounded-xl px-4 py-2 text-sm font-extrabold transition ${
                    lang === value ? "bg-gold text-ink" : "text-mist hover:bg-white/[0.08]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {Object.keys(CITY_CONFIG).map((option) => (
              <GhostButton key={option} active={city === option} onClick={() => setCity(option)}>
                <CheckCircle2 className="h-4 w-4" />
                {option}
              </GhostButton>
            ))}
          </div>
        </header>

        <section className="mb-8 max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.26 }}
            className="text-sm font-bold uppercase tracking-[0.24em] text-gold"
          >
            {t.fastTransparent}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, delay: 0.04 }}
            className="mt-3 font-display text-5xl font-extrabold leading-tight text-white sm:text-6xl"
          >
            {t.heroTitle}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, delay: 0.08 }}
            className="mt-4 max-w-2xl text-lg leading-8 text-mist/82"
          >
            {t.heroText}
          </motion.p>
        </section>

        <AirportPriceBand t={t} />
        <BookingPanel lang={lang} t={t} city={city} setCity={setCity} />
        <ContactSection t={t} />
        <ComplaintsForm t={t} />
      </div>
    </main>
  );
}
